import "./Modal.scss";

import clsx from "clsx";
import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { THEME } from "../../utils/constants";
import { KEYS } from "../../utils/keys";
import { AppState } from "../../utils/types";
import { useDevice, useSchemadrawContainer } from "./App";

export const Modal: React.FC<{
  className?: string;
  children: React.ReactNode;
  maxWidth?: number;
  onCloseRequest(): void;
  labelledBy: string;
  theme?: AppState["theme"];
  closeOnClickOutside?: boolean;
}> = (props) => {
  const { theme = THEME.LIGHT, closeOnClickOutside = true } = props;
  const modalRoot = useBodyRoot(theme);

  if (!modalRoot) {
    return null;
  }

  const handleKeydown = (event: React.KeyboardEvent) => {
    if (event.key === KEYS.ESCAPE) {
      event.nativeEvent.stopImmediatePropagation();
      event.stopPropagation();
      props.onCloseRequest();
    }
  };

  return createPortal(
    <div
      className={clsx("Modal", props.className)}
      role="dialog"
      aria-modal="true"
      onKeyDown={handleKeydown}
      aria-labelledby={props.labelledBy}
      data-prevent-outside-click
    >
      <div
        className="Modal__background"
        onClick={closeOnClickOutside ? props.onCloseRequest : undefined}
      ></div>
      <div
        className="Modal__content"
        style={{ "--max-width": `${props.maxWidth}px` }}
        tabIndex={0}
      >
        {props.children}
      </div>
    </div>,
    modalRoot,
  );
};

const useBodyRoot = (theme: AppState["theme"]) => {
  const [div, setDiv] = useState<HTMLDivElement | null>(null);

  const device = useDevice();
  const isMobileRef = useRef(device.editor.isMobile);
  isMobileRef.current = device.editor.isMobile;

  const { container: schemadrawContainer } = useSchemadrawContainer();

  useLayoutEffect(() => {
    if (div) {
      div.classList.toggle("schemadraw--mobile", device.editor.isMobile);
    }
  }, [div, device.editor.isMobile]);

  useLayoutEffect(() => {
    const isDarkTheme =
      !!schemadrawContainer?.classList.contains("theme--dark") ||
      theme === "dark";
    const div = document.createElement("div");

    div.classList.add("schemadraw", "schemadraw-modal-container");
    div.classList.toggle("schemadraw--mobile", isMobileRef.current);

    if (isDarkTheme) {
      div.classList.add("theme--dark");
      div.classList.add("theme--dark-background-none");
    }
    document.body.appendChild(div);

    setDiv(div);

    return () => {
      document.body.removeChild(div);
    };
  }, [schemadrawContainer, theme]);

  return div;
};
